import React, { createContext, useState, useEffect, useContext } from 'react';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, getCurrentUser, signInWithRedirect, signOut } from '@aws-amplify/auth';
import { loadRuntimeConfig } from '../config/runtimeConfig';

// Create the authentication context
const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export async function bootstrapAuth() {
  const authConfig = await loadRuntimeConfig();
  Amplify.configure({
    Auth: {
      Cognito: {
        region: authConfig.region,
        userPoolId: authConfig.auth.userPoolId,
        userPoolClientId: authConfig.auth.userPoolClientId,
        loginWith: {
          oauth: {
            domain: authConfig.auth.domain,
            scopes: ['email', 'profile', 'openid'],
            redirectSignIn: [window.location.origin + '/callback'],
            redirectSignOut: [window.location.origin],
            responseType: 'code'
          }
        }
      }
    }
  });
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check current authentication state on mount
  useEffect(() => {
    checkAuthState();

    // In Amplify v6, we need to manually check for auth state changes
    const checkInterval = setInterval(() => {
      checkAuthState();
    }, 5000); // Check every 5 seconds

    return () => clearInterval(checkInterval);
  }, []);

  // Check if user is authenticated
  const checkAuthState = async () => {
    try {
      setLoading(true);
      const userData = await getCurrentUser();
      const session = await fetchAuthSession();
      setUser({
        ...userData,
        attributes: session.tokens?.idToken?.payload || {}
      });
      setError(null);
    } catch (err) {
      setUser(null);
      if (err.message !== 'No current user') {
        setError(err.message || 'An error occurred during authentication check');
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign in using Cognito hosted UI
  const signIn = async () => {
    try {
      await signInWithRedirect();
    } catch (err) {
      setError(err.message || 'Failed to sign in');
    }
  };

  // Sign out
  const signOutUser = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (err) {
      setError(err.message || 'Failed to sign out');
    }
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    signIn,
    signOut: signOutUser,
    checkAuthState,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
